(function () {
  'use strict';

  var TARGET = {
    fitted: { min: 0, ideal: 2.5, max: 5 },
    true: { min: 2, ideal: 4.5, max: 7.5 },
    relaxed: { min: 4, ideal: 6.5, max: 9.5 },
    oversized: { min: 6, ideal: 8.5, max: 11.5 }
  };
  var PROGRESS_STEPS = [
    'Building a representative frame…',
    'Matching the selected size to verified dimensions…',
    'Creating the garment preview…',
    'Checking garment and fit accuracy…',
    'Preparing the mirror view…'
  ];
  // The background job can continue after the shopper stops waiting. Bound the
  // foreground experience just beyond the renderer's three-minute generation
  // budget so the fitting room always returns control to the shopper.
  var REQUEST_DEADLINE_MS = 4 * 60 * 1000;
  var MAX_STATUS_FAILURES = 3;
  var EXPERIENCE_MODE = 'live-v1-blank-fresh-r1';
  var DISCLOSURE_TEXT = '9/1/2026 was our official launch — and we’ll keep adding and sharpening features every day to make shopping with us clearer and more reassuring. The Fitting Room is one of them. We’re online-only and make each piece to order, so this is how we help you feel confident about fit before you buy: not just a generated picture, but a growing, honest representation of how our garments are built, how they fall, and how they drape on real human proportions — which vary about as widely as people do. There’s no guarantee this tool will always work — but we’re doing our best to make that the case, every day. Every preview is AI-generated; it is not a photograph of the garment on you.';

  function clamp(number, min, max) {
    return Math.max(min, Math.min(max, number));
  }

  function distance(value, min, max) {
    return value < min ? min - value : value > max ? value - max : 0;
  }

  function round(number, places) {
    return Number(number.toFixed(places));
  }

  // ==========================================================================
  // FITTING-ROOM SIZE GATE — CLIENT MIRROR OF THE WORKER
  // --------------------------------------------------------------------------
  // Proactively disables sizes in the Fitting Room's own size control that the
  // Worker would refuse to render, so a shopper is guided only to the sizes
  // worth deciding between. This is a FAITHFUL, byte-lockstep port of the
  // Worker's authoritative gate in:
  //   services/fresh-fit-renderer/src/fit.js
  //     · SIZE_GATE constants
  //     · calculateFit  (body midpoint + per-size room / fitRange scoring + best)
  //     · lengthLanding severely-short edge (LENGTH_LANDING_BANDS.severelyShortMaxIn)
  //     · sleeveLanding + design-aware sleeve severely-short gate
  //       (SLEEVE_APPLICABILITY, FRAME_ANTHROPOMETRY sleeve ratios,
  //        SLEEVE_LANDING_BANDS; convention from the contract's sleeve_convention)
  //     · evaluateSizeGate
  // It uses the SAME contract data suggestion() reads from the page. The values
  // below MUST stay identical to the Worker's constants so the disabled set
  // matches exactly what the Worker gates at request time. The server's
  // gated-response handler (payload.gated === true, below) remains the backstop.
  // KEEP IN LOCKSTEP with the Worker whenever either side changes.
  // ==========================================================================
  var SIZE_GATE = {
    maxSizeStepsFromSuggested: 2, // |selected.index - suggested.index| <= this
    minChestEaseIn: 1.5,          // chest room below this = too tight
    maxChestEaseIn: 12.5,         // chest room above this = costume-loose
    rangeTightBeyondIn: 1.0,      // body_range: body chest may exceed range max by up to this
    rangeLooseBelowIn: 6.0        // body_range: body chest may fall below range min by up to this
  };
  // From the Worker's LENGTH_LANDING_BANDS — only the severely-short edge gates.
  var LENGTH_SEVERELY_SHORT_MAX_IN = -2.0; // deltaLen < this (+eps) => severely short => gate
  var GATE_UNCERTAINTY_IN = 4.25;          // calculateFit body uncertainty

  // Mirrors the Worker's round(n, p = 1): default precision is ONE decimal.
  function gateRound(number, places) {
    return Number(number.toFixed(places === undefined ? 1 : places));
  }

  // From the Worker's sleeve model (fit.js): SLEEVE_APPLICABILITY,
  // FRAME_ANTHROPOMETRY sleeve ratios, and SLEEVE_LANDING_BANDS. KEEP IN LOCKSTEP.
  var SLEEVE_SHORT_SLEEVE_CUTOFF_IN = 20;      // sleeve <= this is a short/cap sleeve => no landing
  var SLEEVE_TRUST_WINDOW_IN = 4.5;            // |delta vs convention expectation| beyond this = untrusted
  var SLEEVE_SHOULDER_TO_CUFF_RATIO = 0.345;   // shoulder-seam to cuff /H
  var SLEEVE_CENTER_BACK_RATIO = 0.5;          // center-back-neck across shoulder to cuff /H
  var SLEEVE_SEVERELY_SHORT_MAX_IN = -1.5;     // deltaSleeve <= this (+eps) => severely short
  var SLEEVE_SHORT_MAX_IN = -0.5;              // (-1.5,-0.5] a little high
  var SLEEVE_RIGHT_MAX_IN = 1.0;               // (-0.5,1.0) at the wrist
  var SLEEVE_LONG_MAX_IN = 2.5;                // [1.0,2.5) runs long

  // Port of fit.js sleeveLanding(): returns { bucket, severelyShort } or null.
  // Convention comes from the page contract's sleeve_convention (contract source
  // of truth), defaulting to center_back to match the Worker's DEFAULT_SLEEVE_CONVENTION.
  function sleeveLandingFor(garment, heightIn, sleeveVal) {
    if (sleeveVal == null) return null;
    if (sleeveVal <= SLEEVE_SHORT_SLEEVE_CUTOFF_IN) return null;
    var convention = garment.sleeve_convention || 'center_back';
    var ratio = convention === 'shoulder_to_cuff'
      ? SLEEVE_SHOULDER_TO_CUFF_RATIO
      : SLEEVE_CENTER_BACK_RATIO;
    var expected = gateRound(ratio * heightIn, 2);
    var delta = gateRound(sleeveVal - expected, 2);
    if (Math.abs(delta) > SLEEVE_TRUST_WINDOW_IN) return null;
    var bucket;
    if (delta < SLEEVE_SEVERELY_SHORT_MAX_IN + 1e-9) bucket = 'severely_short';
    else if (delta < SLEEVE_SHORT_MAX_IN) bucket = 'short';
    else if (delta < SLEEVE_RIGHT_MAX_IN) bucket = 'right';
    else if (delta < SLEEVE_LONG_MAX_IN) bucket = 'long';
    else bucket = 'very_long';
    return { bucket: bucket, severelyShort: bucket === 'severely_short' };
  }

  // Port of calculateFit's body + candidate build + best selection, restricted
  // to the fields the gate needs (index, length, chest/room OR fitRange).
  function fitEngine(garment, heightIn, weightLb) {
    var midpoint = clamp(0.205 * heightIn + 0.105 * weightLb + 9.2, 28, 70);
    var body = {
      min: gateRound(clamp(midpoint - GATE_UNCERTAINTY_IN, 28, 70), 2),
      midpoint: gateRound(midpoint, 2),
      max: gateRound(clamp(midpoint + GATE_UNCERTAINTY_IN, 30, 74), 2)
    };
    var target = TARGET[garment.cut] || TARGET.true;
    var candidates = garment.sizes.map(function (sizeName, index) {
      var base = { size: sizeName, index: index, length: garment.length[sizeName], sleeve: garment.sleeve && garment.sleeve[sizeName] };
      if (garment.chest_type === 'body_range') {
        var fitMin = garment.fit_chest_min[sizeName];
        var fitMax = garment.fit_chest_max[sizeName];
        base.fitRange = { min: fitMin, max: fitMax };
        base.score = gateRound(distance(body.midpoint, fitMin, fitMax), 3);
        return base;
      }
      var chest = garment.chest_full[sizeName];
      var roomFit = {
        min: gateRound(chest - body.max),
        midpoint: gateRound(chest - body.midpoint),
        max: gateRound(chest - body.min)
      };
      var score = distance(roomFit.midpoint, target.min, target.max)
        + Math.abs(roomFit.midpoint - target.ideal) * 0.05
        + (roomFit.max < 0 ? Math.abs(roomFit.max) * 8 : 0);
      base.chest = chest;
      base.room = roomFit;
      base.score = gateRound(score, 3);
      return base;
    }).sort(function (a, b) {
      return a.score - b.score || a.index - b.index;
    });
    return { body: body, candidates: candidates, best: candidates[0] };
  }

  // Port of evaluateSizeGate for one candidate size against the engine's best.
  function evaluateGateFor(garment, engine, selected, heightIn) {
    var best = engine.best;
    var sizeSteps = selected.index - best.index; // = fit.comparison.sizeSteps
    var reasons = [];
    if (Math.abs(sizeSteps) > SIZE_GATE.maxSizeStepsFromSuggested) {
      reasons.push(sizeSteps > 0 ? 'beyond_window_larger' : 'beyond_window_smaller');
    }
    if (selected.room && selected.chest != null) {
      var ease = selected.room.midpoint;
      if (ease < SIZE_GATE.minChestEaseIn) reasons.push('chest_too_tight');
      if (ease > SIZE_GATE.maxChestEaseIn) reasons.push('chest_costume_loose');
    } else {
      var rangeMin = selected.fitRange.min;
      var rangeMax = selected.fitRange.max;
      if (engine.body.midpoint > rangeMax + SIZE_GATE.rangeTightBeyondIn) reasons.push('chest_too_tight');
      if (engine.body.midpoint < rangeMin - SIZE_GATE.rangeLooseBelowIn) reasons.push('chest_costume_loose');
    }
    // lengthLanding severely-short: delta vs the suggested size's own length.
    var deltaLen = Number((selected.length - best.length).toFixed(2));
    if (deltaLen < LENGTH_SEVERELY_SHORT_MAX_IN + 1e-9) reasons.push('length_severely_short');
    // Sleeve severely-short gate (design-aware; mirror of fit.js evaluateSizeGate).
    // Gates only when the SELECTED size rides severely short AND the SUGGESTED
    // size lands acceptably (wrist or longer). The suggested size is exempt
    // (sizeSteps !== 0). Cat-4 garments carry sleeve_gate_untrusted on the page
    // contract and never sleeve-gate. Short/implausible sleeves yield no landing.
    if (sizeSteps !== 0 && !garment.sleeve_gate_untrusted) {
      var selSleeve = sleeveLandingFor(garment, heightIn, selected.sleeve);
      if (selSleeve && selSleeve.severelyShort) {
        var sugSleeve = sleeveLandingFor(garment, heightIn, best.sleeve);
        var sugAcceptable = sugSleeve != null
          && sugSleeve.bucket !== 'short' && sugSleeve.bucket !== 'severely_short';
        if (sugAcceptable) reasons.push('sleeve_severely_short');
      }
    }
    return { size: selected.size, gated: reasons.length > 0, reasons: reasons };
  }

  // Per-size verdict map + the suggested size, from the same page contract data.
  function gateVerdicts(garment, heightIn, weightLb) {
    var engine = fitEngine(garment, heightIn, weightLb);
    var out = {};
    engine.candidates.forEach(function (candidate) {
      out[candidate.size] = evaluateGateFor(garment, engine, candidate, heightIn);
    });
    return { verdicts: out, best: engine.best.size };
  }

  // Accessible, claims-safe reason text (gate spirit; no "AI"/"generated").
  function gateReasonText(verdict, suggestedSize) {
    var reasons = verdict.reasons || [];
    var runsLarge = reasons.indexOf('beyond_window_larger') > -1 || reasons.indexOf('chest_costume_loose') > -1;
    var runsSmall = reasons.indexOf('beyond_window_smaller') > -1 || reasons.indexOf('chest_too_tight') > -1 || reasons.indexOf('length_severely_short') > -1 || reasons.indexOf('sleeve_severely_short') > -1;
    var direction = runsLarge && !runsSmall ? ' (it runs too large on this frame)'
      : runsSmall && !runsLarge ? ' (it runs too small on this frame)'
      : '';
    return 'Size ' + verdict.size + " isn't a realistic fit on this frame" + direction
      + ", so the Fitting Room doesn't show it. The most accurate guidance on sizing is to"
      + ' reference the finished measurements on each product page.';
  }

  function suggestion(garment, height, weight) {
    var midpoint = clamp(0.205 * height + 0.105 * weight + 9.2, 28, 70);
    var target = TARGET[garment.cut] || TARGET.true;
    return garment.sizes.map(function (size, index) {
      var score;
      if (garment.chest_type === 'body_range') {
        score = distance(midpoint, garment.fit_chest_min[size], garment.fit_chest_max[size]);
      } else {
        // Keep the browser's provisional suggestion byte-for-byte aligned with
        // the authoritative fit engine, which scores room at one decimal.
        var room = round(garment.chest_full[size] - midpoint, 1);
        score = distance(room, target.min, target.max) + Math.abs(room - target.ideal) * 0.05;
      }
      return { size: size, index: index, score: round(score, 3) };
    }).sort(function (a, b) {
      return a.score - b.score || a.index - b.index;
    })[0].size;
  }

  function requestError(payload, fallback) {
    var error = new Error(payload && payload.message ? payload.message : fallback);
    error.code = payload && payload.code ? payload.code : 'preview_unavailable';
    error.retryable = Boolean(payload && payload.retryable);
    error.retryAfterSeconds = Math.max(0, parseInt(payload && payload.retryAfterSeconds, 10) || 0);
    return error;
  }

  function addRetryHint(payload, response) {
    var hinted = parseInt(response && response.headers && response.headers.get('retry-after'), 10);
    if (Number.isFinite(hinted) && hinted > 0) payload.retryAfterSeconds = hinted;
    return payload;
  }

  function retryableStatus(status) {
    return status === 404 || status === 408 || status === 425 || status === 429 || status >= 500;
  }

  async function responsePayload(response) {
    var raw = await response.text();
    return raw ? JSON.parse(raw) : {};
  }

  function requestStatus(url, signal) {
    return new Promise(function (resolve, reject) {
      var xhr = new XMLHttpRequest();
      var onAbort = function () { xhr.abort(); };

      function cleanup() {
        if (signal) signal.removeEventListener('abort', onAbort);
      }

      xhr.open('GET', url, true);
      xhr.timeout = 15000;
      xhr.setRequestHeader('Accept', 'application/json');
      xhr.onload = function () {
        cleanup();
        var payload;
        try {
          payload = xhr.responseText ? JSON.parse(xhr.responseText) : {};
        } catch (error) {
          reject(error);
          return;
        }
        resolve({
          ok: xhr.status >= 200 && xhr.status < 300,
          status: xhr.status,
          payload: payload,
          retryAfter: parseInt(xhr.getResponseHeader('retry-after'), 10) || 0
        });
      };
      xhr.onerror = function () {
        cleanup();
        reject(new Error('preview_status_network_error'));
      };
      xhr.ontimeout = function () {
        cleanup();
        reject(new Error('preview_status_timeout'));
      };
      xhr.onabort = function () {
        cleanup();
        var error = new Error('preview_status_aborted');
        error.name = 'AbortError';
        reject(error);
      };
      if (signal) {
        if (signal.aborted) {
          cleanup();
          var aborted = new Error('preview_status_aborted');
          aborted.name = 'AbortError';
          reject(aborted);
          return;
        }
        signal.addEventListener('abort', onAbort, { once: true });
      }
      xhr.send();
    });
  }

  function friendlyFailure(error) {
    // Photo-mode and rate-limit responses carry their own precise, claims-safe copy —
    // surface it instead of the generic line.
    if (error && (error.code === 'photo_preview_failed_qa' || error.code === 'photo_preview_unavailable' || error.code === 'prototype_request_limit_reached') && error.message) {
      return error.message;
    }
    if (error && error.code === 'provider_visual_quality_exhausted') {
      return 'This image did not pass our garment and fit accuracy checks, so we did not show it. Please try again.';
    }
    if (error && error.code === 'provider_capacity_exhausted') {
      return 'The preview service is busy right now. Your selections are still here—please try again.';
    }
    if (error && error.code === 'provider_artifact_invalid') {
      return 'The generated image did not pass our file checks, so we did not show it. Your selections are still here—please try again.';
    }
    if (error && error.code === 'preview_timeout') {
      return 'This preview is taking longer than usual. Your selections are still here—please try again.';
    }
    if (error && error.code === 'provider_generation_deadline_exceeded') {
      return 'We couldn’t finish an accurate preview within this attempt. Your selections are still here—please try again.';
    }
    if (error && error.code === 'preview_status_unavailable') {
      return 'We lost contact with this preview. Your selections are still here—please try again.';
    }
    return 'We couldn’t finish the representative preview. Your selections are still here—please try again.';
  }

  function init(root) {
    var endpoint = root.dataset.wsFreshFitEndpoint;
    var contractId = root.dataset.wsContractId;
    var dataNode = root.querySelector('#ws-fit-data');
    var garment;
    try {
      garment = JSON.parse(dataNode.textContent);
    } catch (error) {
      return;
    }

    var overlay = root.querySelector('[data-ws-fitroom-overlay]');
    var open = root.querySelector('[data-ws-fitroom-open]');
    var close = root.querySelector('[data-ws-fitroom-close]');
    var height = root.querySelector('[data-ws-simple-height]');
    var weight = root.querySelector('[data-ws-simple-weight]');
    var size = root.querySelector('[data-ws-simple-size]');
    var sizeDisplay = root.querySelector('[data-ws-simple-size-display]');
    var sizeLabel = root.querySelector('[data-ws-simple-size-label]');
    var button = root.querySelector('[data-ws-fresh-fit-generate]');
    var identityBlock = root.querySelector('[data-ws-fit-identity]');
    var gender = root.querySelector('[data-ws-fit-gender]');
    var genderSelf = root.querySelector('[data-ws-fit-gender-self]');
    var ethnicity = root.querySelector('[data-ws-fit-ethnicity]');
    var ethnicitySelf = root.querySelector('[data-ws-fit-ethnicity-self]');
    var photoInput = root.querySelector('[data-ws-fit-photo]');
    var photoRemove = root.querySelector('[data-ws-fit-photo-remove]');
    var dropzone = root.querySelector('[data-ws-fit-dropzone]');
    var dropzoneText = root.querySelector('[data-ws-fit-dropzone-text]');
    var dropzoneThumb = root.querySelector('[data-ws-fit-dropzone-thumb]');
    var photoDataUrl = null;
    var northstarMode = root.dataset.wsNorthstar === 'true';
    // DORMANT until founder ratifies suggestion trust per family (verdict sweeps): when the
    // worker's product-views response carries suggestionsTrusted:true, the engine's pick is
    // shown as 'Suggested' and pre-selected. Until then this stays fully invisible (LAW:
    // no customer-facing suggestions until founder-granted trust).
    var suggestionsTrusted = false;
    var productHandle = root.dataset.wsProductHandle || '';
    var productTitle = root.dataset.wsFitProductTitle || 'this garment';
    var variantTitle = root.dataset.wsVariantTitle || '';
    var status = root.querySelector('[data-ws-fresh-fit-status]');
    var media = root.querySelector('[data-ws-fresh-fit-media]');
    var image = root.querySelector('[data-ws-fresh-fit-image]');
    var requestId = 0;
    var phaseTimers = [];
    var retryTimer = null;
    var opener = null;
    var suggestedSize = '';
    var sizeTouched = false;
    // Internal-activity flag (2026-09-21): visit any PDP with ?ws_internal=1 once on a device to
    // mark its Fitting Room events as internal; ?ws_internal=0 clears it. Local to the device.
    function isInternal() {
      try {
        var q = new URLSearchParams(window.location.search).get('ws_internal');
        if (q === '1') window.localStorage.setItem('ws-internal', '1');
        else if (q === '0') window.localStorage.removeItem('ws-internal');
        return window.localStorage.getItem('ws-internal') === '1';
      } catch (e) { return false; }
    }
    // Remembered frame (2026-09-21): height and weight persist on THIS device only, so the frame
    // carries across product pages. Representation choices are deliberately NOT remembered.
    var FRAME_KEY = 'ws-fitroom-frame';
    function loadFrame() {
      try { return JSON.parse(window.localStorage.getItem(FRAME_KEY) || 'null'); } catch (e) { return null; }
    }
    function saveFrame(frame) {
      try { window.localStorage.setItem(FRAME_KEY, JSON.stringify(frame)); } catch (e) { /* storage may be unavailable */ }
    }
    function forgetFrame() {
      try { window.localStorage.removeItem(FRAME_KEY); } catch (e) { /* ignore */ }
    }
    // PDP size alignment (2026-09-21): the purchase selector's labels (XXL) and the contract's
    // labels (2XL) name the same size. Resolve deliberately, never by guess.
    var SIZE_ALIASES = { 'XXL': '2XL', 'XXXL': '3XL', 'XXXXL': '4XL', '2X': '2XL', '3X': '3XL', '4X': '4XL', 'XXS': '2XS' };
    function contractSizeFor(label) {
      var u = String(label || '').trim().toUpperCase().replace(/\s+/g, '');
      return SIZE_ALIASES[u] || u;
    }
    var sizesOffered = (root.dataset.wsFitSizesOffered || '').split(',').map(function (v) { return v.trim(); }).filter(Boolean);
    var pdpLabelByContract = {};
    sizesOffered.forEach(function (label) { pdpLabelByContract[contractSizeFor(label)] = label; });
    var pdpSizeOptionIndex = parseInt(root.dataset.wsFitSizeOption || '0', 10);
    function pdpSelectedContractSize() {
      var checked = pdpSizeOptionIndex ? document.querySelector('.ws-buybox__radio[data-ws-option-index="' + pdpSizeOptionIndex + '"]:checked') : null;
      var label = checked ? checked.value : (root.dataset.wsFitSelectedSize || '');
      return label ? contractSizeFor(label) : '';
    }
    // Measurement (roadmap #1): Fitting Room events into GA4/Clarity. Best-effort only.
    // Disposition per open (law L3, 2026-09-26): attempts can outnumber opens (a shopper tries a
    // second size), so counting render/gated/error events cannot say what happened to each OPEN.
    // Every open now ends in exactly one ws_fitting_room_end_<outcome> event, best outcome wins:
    // rendered > gated > errored > abandoned (closed or left without any outcome).
    var fitOpen = false;
    var fitOutcome = null;
    function noteOutcome(name) {
      if (name === 'ws_fitting_room_render') fitOutcome = 'rendered';
      else if (name === 'ws_fitting_room_gated' && fitOutcome !== 'rendered') fitOutcome = 'gated';
      else if (name === 'ws_fitting_room_error' && !fitOutcome) fitOutcome = 'errored';
    }
    function endOpen() {
      if (!fitOpen) return;
      fitOpen = false;
      track('ws_fitting_room_end_' + (fitOutcome || 'abandoned'));
    }
    window.addEventListener('pagehide', endOpen);
    function track(name, params) {
      if (fitOpen) noteOutcome(name);
      try {
        var payload = Object.assign({ product: productHandle || contractId || '', mode: photoDataUrl ? 'photo' : 'standard' }, params || {});
        if (isInternal()) payload.ws_internal = 1; // our own testing, separable in GA4 reports
        if (typeof window.gtag === 'function') window.gtag('event', name, payload);
        else if (Array.isArray(window.dataLayer)) window.dataLayer.push(Object.assign({ event: name }, payload));
        if (typeof window.clarity === 'function') window.clarity('set', name, String(payload.size || payload.vote || payload.view || '1'));
      } catch (e) { /* analytics must never break the fitting room */ }
    }
    var viewSelect = null;
    var viewField = null;
    var colorSelect = null;
    // Front/Back toggle appears only for products with verified BACK truth (big back
    // embellishments). Hidden in photo mode - a front-facing photo cannot show a back.
    if (northstarMode && productHandle) {
      try {
        fetch(endpoint.replace(/\/previews$/, '/product-views') + '?productHandle=' + encodeURIComponent(productHandle))
          .then(function (r) { return r.json(); })
          .then(function (views) {
            var row = root.querySelector('.ws-fitroom-controls__row');
            if (!row || !views) return;
            // No verified front truth for this product (2026-09-26): the worker refuses it without
            // spending, so do not offer a door that can only lead to a refusal.
            if (views.front === false && !(views.colors && views.colors.length)) {
              root.style.display = 'none'; // inline style: the theme's display rules outrank [hidden]
              // The gallery cue may be placed after this runs, so hide it by rule, not by element.
              var cueOff = document.createElement('style');
              cueOff.textContent = '.ws-fitroom-cue{display:none!important}';
              document.head.appendChild(cueOff);
              return;
            }
            // Color selector (founder 2026-09-06): pick any colorway with verified truth.
            suggestionsTrusted = views.suggestionsTrusted === true;
            if (suggestionsTrusted && suggestedSize) {
              size.value = suggestedSize;
              if (sizeLabel) sizeLabel.textContent = 'Suggested size';
              refreshSizeRead();
            }
            if (views.colors && views.colors.length > 1) {
              var colorField = document.createElement('div');
              colorField.className = 'ws-fitroom-field';
              colorField.innerHTML = '<label class="ws-fitroom-label">Color</label>'
                + '<select class="ws-fitroom-control" data-ws-fit-color aria-label="Garment color"></select>';
              row.appendChild(colorField);
              colorSelect = colorField.querySelector('[data-ws-fit-color]');
              views.colors.forEach(function (c) {
                var o = document.createElement('option');
                o.value = c; o.textContent = c;
                if (variantTitle && c.toLowerCase() === variantTitle.toLowerCase()) o.selected = true;
                colorSelect.appendChild(o);
              });
              colorSelect.addEventListener('change', resetPreview);
            }
            if (!views.back) return;
            viewField = document.createElement('div');
            viewField.className = 'ws-fitroom-field';
            viewField.innerHTML = '<label class="ws-fitroom-label">View</label>'
              + '<select class="ws-fitroom-control" data-ws-fit-view aria-label="Garment view">'
              + '<option value="front">Front</option><option value="back">Back</option></select>';
            row.appendChild(viewField);
            viewSelect = viewField.querySelector('[data-ws-fit-view]');
            viewSelect.addEventListener('change', resetPreview);
          })
          .catch(function () { /* view toggle is progressive enhancement */ });
      } catch (e) { /* ignore */ }
    }
    var feedbackBox = root.querySelector('[data-ws-fit-feedback]');
    var feedbackQ = root.querySelector('[data-ws-fit-feedback-q]');
    var feedbackButtons = Array.prototype.slice.call(root.querySelectorAll('[data-ws-fit-vote]'));
    var feedbackRenderKey = null;
    function showFeedback(renderKey) {
      if (!feedbackBox) return;
      feedbackRenderKey = renderKey || null;
      if (feedbackQ) feedbackQ.textContent = 'Was this helpful for making a decision?';
      feedbackButtons.forEach(function (b) { b.hidden = false; b.disabled = false; });
      feedbackBox.hidden = false;
    }
    function hideFeedback() { if (feedbackBox) feedbackBox.hidden = true; }
    feedbackButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        feedbackButtons.forEach(function (b) { b.disabled = true; b.hidden = true; });
        track('ws_fitting_room_thumbs', { vote: btn.dataset.wsFitVote });
        if (feedbackQ) feedbackQ.textContent = 'Thanks — noted.';
        try {
          fetch(endpoint.replace(/\/previews$/, '/feedback'), {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({
              vote: btn.dataset.wsFitVote,
              renderKey: feedbackRenderKey,
              productHandle: productHandle || null,
              photoMode: Boolean(photoDataUrl),
              experienceMode: northstarMode ? 'northstar-v1' : EXPERIENCE_MODE
            })
          });
        } catch (e) { /* feedback is best-effort */ }
      });
    });
    var measureContext = null;
    // Browsers size a <select> to its WIDEST option; fit each control to its
    // SELECTED text instead so the boxes carry uniform padding, no dead space.
    function fitSelectWidth(sel) {
      if (!sel || !sel.options.length) return;
      try {
        // Measure with a real DOM node (canvas underestimates on Safari) + a buffer.
        var style = getComputedStyle(sel);
        var probe = document.createElement('span');
        probe.style.position = 'absolute';
        probe.style.visibility = 'hidden';
        probe.style.whiteSpace = 'pre';
        probe.style.fontFamily = style.fontFamily;
        probe.style.fontSize = style.fontSize;
        probe.style.fontWeight = style.fontWeight;
        probe.style.letterSpacing = style.letterSpacing;
        probe.textContent = sel.options[sel.selectedIndex] ? sel.options[sel.selectedIndex].text : '';
        document.body.appendChild(probe);
        var textWidth = probe.getBoundingClientRect().width;
        probe.remove();
        var pad = parseFloat(style.paddingLeft || 0) + parseFloat(style.paddingRight || 0)
          + parseFloat(style.borderLeftWidth || 0) + parseFloat(style.borderRightWidth || 0);
        sel.style.width = Math.max(64, Math.ceil(textWidth + pad + 10)) + 'px';
      } catch (e) { /* sizing is cosmetic; never break the fitting room */ }
    }
    function fitAllSelects() {
      // Widths are owned by the CSS grid now (equal boxes, edge-aligned rows) — just
      // clear any inline width so the stylesheet always wins.
      [height, weight, size, gender, ethnicity].forEach(function (sel) {
        if (sel) sel.style.removeProperty('width');
      });
    }

    (function injectDisclosure() {
      try {
      if (!open || !open.parentNode || document.getElementById('ws-fr-disclosure-style')) return;
      var st = document.createElement('style');
      st.id = 'ws-fr-disclosure-style';
      st.textContent = '.ws-fr-info{position:relative;display:inline-flex;align-items:center;justify-content:center;width:16px;height:16px;margin-left:.45em;border:1px solid currentColor;border-radius:50%;font:italic 600 11px/1 -apple-system,system-ui,sans-serif;color:inherit;opacity:.5;cursor:help;vertical-align:middle;background:none}'
        + '.ws-fr-info:hover,.ws-fr-info:focus,.ws-fr-info:focus-within{opacity:1;outline:none}'
        + '.ws-fr-info__tip{position:absolute;bottom:calc(100% + 6px);left:50%;transform:translateX(-50%);width:min(320px,78vw);padding:.8rem .9rem;border-radius:10px;background:#16222b;color:#f5f2eb;font:normal 400 12.5px/1.55 -apple-system,system-ui,sans-serif;letter-spacing:.005em;text-align:left;box-shadow:0 10px 28px rgba(0,0,0,.3);opacity:0;visibility:hidden;transition:opacity .16s ease;z-index:70}'
        + '.ws-fr-info:hover .ws-fr-info__tip,.ws-fr-info:focus .ws-fr-info__tip,.ws-fr-info:focus-within .ws-fr-info__tip{opacity:1;visibility:visible}';
      document.head.appendChild(st);
      var info = document.createElement('span');
      info.className = 'ws-fr-info';
      info.setAttribute('tabindex', '0');
      info.setAttribute('role', 'note');
      info.setAttribute('aria-label', DISCLOSURE_TEXT);
      info.textContent = 'i';
      var tip = document.createElement('span');
      tip.className = 'ws-fr-info__tip';
      tip.setAttribute('role', 'tooltip');
      tip.textContent = DISCLOSURE_TEXT;
      info.appendChild(tip);
      open.parentNode.insertBefore(info, open.nextSibling);
      } catch (e) { /* disclosure is cosmetic; never break the fitting room */ }
    })();

    garment.sizes.forEach(function (value) {
      // Only sizes this product actually sells appear in the Fitting Room (the contract can run
      // wider than the purchase selector, e.g. 3XL/4XL on a product sold to XXL).
      if (sizesOffered.length && !pdpLabelByContract[value]) return;
      var option = document.createElement('option');
      option.value = value;
      option.textContent = pdpLabelByContract[value] || value;
      size.appendChild(option);
    });
    var applyBtn = root.querySelector('[data-ws-fit-apply-size]');
    var aiCaption = root.querySelector('[data-ws-fit-ai-caption]');
    var forgetBtn = root.querySelector('[data-ws-fit-forget]');
    function pageLabel(contractSize) { return pdpLabelByContract[contractSize] || contractSize; }
    function refreshApply() {
      if (!applyBtn) return;
      applyBtn.disabled = false;
      applyBtn.textContent = 'Use size ' + pageLabel(size.value) + ' on this page';
    }
    function showAiCaption(previewProfile, renderedSize) {
      var previewIsPhoto = Boolean(previewProfile && previewProfile.photo);
      var previewSize = pageLabel(renderedSize || previewProfile.selectedSize);
      var previewChoice = productTitle + ', size ' + previewSize;
      if (previewProfile.variantTitle) previewChoice += ', ' + previewProfile.variantTitle;
      if (previewProfile.view) previewChoice += ', ' + (previewProfile.view === 'back' ? 'back' : 'front') + ' view';
      image.alt = (previewIsPhoto ? 'AI-generated edit of your uploaded photo' : 'AI-generated representative preview')
        + ' requested for ' + previewChoice + '. Not a photograph of the garment on you.';
      if (aiCaption) {
        aiCaption.textContent = previewIsPhoto
          ? 'AI-generated edit of your photo wearing size ' + previewSize + '.'
          : 'AI-generated preview of a representative frame wearing size ' + previewSize + '. Not a photograph of the garment on you.';
        aiCaption.hidden = false;
      }
      if (applyBtn) { refreshApply(); applyBtn.hidden = false; }
    }
    function hideAiCaption() {
      if (aiCaption) aiCaption.hidden = true;
      if (applyBtn) applyBtn.hidden = true;
    }
    // Explicit handoff: the shopper applies the compared size to the purchase selector on purpose.
    if (applyBtn) applyBtn.addEventListener('click', function () {
      var label = pageLabel(size.value);
      var radios = pdpSizeOptionIndex ? document.querySelectorAll('.ws-buybox__radio[data-ws-option-index="' + pdpSizeOptionIndex + '"]') : [];
      var hit = null;
      for (var r = 0; r < radios.length; r++) { if (contractSizeFor(radios[r].value) === size.value) { hit = radios[r]; break; } }
      if (hit) {
        hit.checked = true;
        hit.dispatchEvent(new Event('change', { bubbles: true }));
        var lab = document.querySelector('label[for="' + hit.id + '"]');
        if (lab) lab.click();
        applyBtn.textContent = 'Size ' + label + ' selected on this page';
        applyBtn.disabled = true;
        track('ws_fitting_room_apply_size', { size: size.value });
      } else {
        applyBtn.textContent = 'Size ' + label + ' is not available on this page';
        applyBtn.disabled = true;
      }
    });
    if (forgetBtn) forgetBtn.addEventListener('click', function () {
      forgetFrame();
      forgetBtn.textContent = 'Forgotten on this device';
      forgetBtn.disabled = true;
    });

    function show() {
      endOpen();
      track('ws_fitting_room_open');
      fitOpen = true;
      fitOutcome = null;
      setTimeout(prerender, 200);
      opener = open;
      overlay.classList.add('is-open');
      overlay.setAttribute('aria-hidden', 'false');
      if (!overlay.open) overlay.showModal();
      document.body.style.overflow = 'hidden';
      close.focus();
    }

    function hide() {
      endOpen();
      if (overlay.open) overlay.close();
      overlay.classList.remove('is-open');
      overlay.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (opener) opener.focus();
      opener = null;
    }

    function profile(persist) {
      if (persist !== false) try {
        // 2026-09-25: representation choices are NOT remembered. The visible caveat says "Your
        // height and weight are remembered on this device only" -- persisting gender/ethnicity
        // here made that sentence false about protected-class data. Frame only.
        saveFrame({
          heightIn: parseInt(height.value, 10), weightLb: parseInt(weight.value, 10),
          savedAt: new Date().toISOString()
        });
      } catch (e) { /* never block a render on storage */ }
      var body = {
        contractId: contractId,
        heightIn: parseInt(height.value, 10),
        weightLb: parseInt(weight.value, 10),
        selectedSize: size.value,
        experienceMode: northstarMode ? 'northstar-v1' : EXPERIENCE_MODE
      };
      if (isInternal()) body.wsInternal = 1; // worker files our own testing under the 'staff' spend lane
      if (northstarMode) {
        if (productHandle) body.productHandle = productHandle;
        body.variantTitle = (colorSelect && colorSelect.value) || variantTitle || undefined;
        body.view = (viewSelect && viewSelect.value === 'back') ? 'back' : 'front';
        if (photoDataUrl) {
          // Zero-persistence photo path: the photo rides only in this request;
          // server and provider retention are not established by this client code.
          body.photo = photoDataUrl;
        } else {
          var g = gender ? gender.value : '';
          if (g === 'self' && genderSelf && genderSelf.value.trim()) body.gender = genderSelf.value.trim();
          else if (g && g !== 'self') body.gender = g;
          var e = ethnicity ? ethnicity.value : '';
          if (e === 'self' && ethnicitySelf && ethnicitySelf.value.trim()) body.ethnicity = ethnicitySelf.value.trim();
          else if (e && e !== 'self') body.ethnicity = e;
        }
      }
      return body;
    }

    // Intent pre-render (2026-09-26). Opening the room is the strongest signal we get, and the
    // shopper then spends time reading and choosing before pressing the button. Start the render
    // for what the controls already say (remembered/default frame, resting size) the moment the
    // room opens. Nothing is shown from this request: if the shopper presses the button with the
    // same choices, the worker attaches them to the in-flight job or serves the finished,
    // QA-passed image; if they change anything, their own request renders as before. One per
    // distinct choice per page, at most two per page, never in photo mode.
    var prerendered = {};
    var prerenderCount = 0;
    function prerender() {
      try {
        if (!northstarMode || photoDataUrl || prerenderCount >= 2) return;
        if (!size.value || (size.selectedOptions && size.selectedOptions[0] && size.selectedOptions[0].disabled)) return;
        var body = profile(false);
        var key = JSON.stringify(body);
        if (prerendered[key]) return;
        prerendered[key] = true;
        prerenderCount += 1;
        track('ws_fitting_room_prerender', { size: body.selectedSize });
        fetch(endpoint, { method: 'POST', headers: { 'content-type': 'application/json' }, body: key, cache: 'no-store' })
          .catch(function () { /* the shopper's own request is unaffected */ });
      } catch (e) { /* never break the room */ }
    }

    if (gender) gender.addEventListener('change', function () {
      if (genderSelf) genderSelf.hidden = gender.value !== 'self';
      resetPreview();
    });
    if (ethnicity) ethnicity.addEventListener('change', function () {
      if (ethnicitySelf) ethnicitySelf.hidden = ethnicity.value !== 'self';
      resetPreview();
    });
    function applyPhotoFile(file) {
      if (!file || String(file.type).indexOf('image/') !== 0) return;
      if (file.size > 8 * 1024 * 1024) {
        status.textContent = 'That photo is over the 8 MB limit — please choose a smaller one (JPEG, PNG, or WEBP).';
        status.dataset.state = 'gated';
        status.classList.remove('ws-visually-hidden');
        return;
      }
      var reader = new FileReader();
      reader.onload = function () {
        photoDataUrl = reader.result;
        track('ws_fitting_room_photo_upload');
        // Founder spec: a personal photo supersedes the identity selections.
        if (identityBlock) identityBlock.hidden = true;
        if (photoRemove) photoRemove.hidden = false;
        if (dropzoneThumb) { dropzoneThumb.src = photoDataUrl; dropzoneThumb.hidden = false; }
        if (dropzoneText) dropzoneText.textContent = file.name.length > 34 ? file.name.slice(0, 31) + '\u2026' : file.name;
        resetPreview();
      };
      reader.readAsDataURL(file);
    }
    if (photoInput) photoInput.addEventListener('change', function () {
      applyPhotoFile(photoInput.files && photoInput.files[0]);
    });
    if (dropzone) {
      dropzone.addEventListener('click', function () { if (photoInput) photoInput.click(); });
      dropzone.addEventListener('keydown', function (event) {
        if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); if (photoInput) photoInput.click(); }
      });
      ['dragover', 'dragenter'].forEach(function (name) {
        dropzone.addEventListener(name, function (event) { event.preventDefault(); dropzone.classList.add('is-dragover'); });
      });
      ['dragleave', 'dragend'].forEach(function (name) {
        dropzone.addEventListener(name, function () { dropzone.classList.remove('is-dragover'); });
      });
      dropzone.addEventListener('drop', function (event) {
        event.preventDefault();
        dropzone.classList.remove('is-dragover');
        applyPhotoFile(event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0]);
      });
    }
    if (photoRemove) photoRemove.addEventListener('click', function () {
      photoDataUrl = null;
      if (photoInput) photoInput.value = '';
      if (identityBlock) identityBlock.hidden = false;
      photoRemove.hidden = true;
      if (dropzoneThumb) { dropzoneThumb.hidden = true; dropzoneThumb.removeAttribute('src'); }
      if (dropzoneText) dropzoneText.textContent = 'Drag & drop a photo here, or click to browse';
      resetPreview();
    });

    function clearPhases() {
      phaseTimers.forEach(function (timer) {
        clearTimeout(timer);
      });
      phaseTimers = [];
      button.classList.remove('is-changing');
    }

    function phase(id, index) {
      if (id !== requestId) return;
      button.classList.add('is-changing');
      phaseTimers.push(setTimeout(function () {
        if (id !== requestId) return;
        button.textContent = PROGRESS_STEPS[index];
        button.classList.remove('is-changing');
        phaseTimers.push(setTimeout(function () {
          phase(id, (index + 1) % PROGRESS_STEPS.length);
        }, 7200));
      }, 220));
    }

    function startPhases(id, selectedSize) {
      clearPhases();
      button.setAttribute('aria-busy', 'true');
      status.dataset.state = 'loading';
      status.textContent = 'Creating a representative preview of a frame like yours wearing size ' + selectedSize + '. We check the garment and fit before showing it. Your size guidance is ready while we work.';
      status.classList.remove('ws-visually-hidden');
      phase(id, 0);
    }

    function stopPhases() {
      clearPhases();
      button.removeAttribute('aria-busy');
      delete status.dataset.state;
    }

    function clearRetryCountdown() {
      if (retryTimer) clearInterval(retryTimer);
      retryTimer = null;
    }

    function startRetryCountdown(id, seconds) {
      clearRetryCountdown();
      var remaining = Math.max(1, Math.min(300, seconds));
      button.disabled = true;

      function render() {
        if (id !== requestId) {
          clearRetryCountdown();
          return;
        }
        if (remaining <= 0) {
          clearRetryCountdown();
          button.disabled = false;
          button.textContent = 'Try again';
          return;
        }
        button.textContent = 'Try again in ' + remaining + 's';
        remaining -= 1;
      }

      render();
      retryTimer = setInterval(render, 1000);
    }

    function refreshSizeRead() {
      // LAW (founder, 2026-09-04): no customer-facing "suggested size" until the
      // founder grants confidence. The control is always just "Size"; the standing
      // guidance is the finished-measurements disclosure rendered in the snippet.
      if (sizeDisplay) sizeDisplay.textContent = size.value;
      if (sizeLabel) sizeLabel.textContent = (suggestionsTrusted && size.value === suggestedSize) ? 'Suggested size' : 'Size';
      fitAllSelects();
    }

    // Proactively disable, in the FITTING-ROOM size control ONLY, the sizes the
    // Worker would refuse to render — so the shopper is guided to the sizes
    // worth deciding between. The add-to-cart / buy size selector is a separate
    // theme control this script never touches; all sizes stay purchasable.
    function applyGate() {
      // Northstar-v1 runs the founder-truth calibration server-side; this client
      // mirror is blank-path math and would disable the wrong sizes. Fail open —
      // the Worker's gated response (honest message, no suggestion) is the backstop.
      if (northstarMode) {
        for (var n = 0; n < size.options.length; n++) {
          size.options[n].disabled = false;
          size.options[n].removeAttribute('aria-label');
          size.options[n].removeAttribute('title');
        }
        return;
      }
      var heightIn = parseInt(height.value, 10);
      var weightLb = parseInt(weight.value, 10);
      var result;
      try {
        result = gateVerdicts(garment, heightIn, weightLb);
      } catch (error) {
        // Fail OPEN: never block try-on if the client gate errors. The Worker
        // gated-response handler remains the backstop.
        for (var r = 0; r < size.options.length; r++) {
          size.options[r].disabled = false;
          size.options[r].removeAttribute('aria-label');
          size.options[r].removeAttribute('title');
        }
        return;
      }
      var verdicts = result.verdicts;
      for (var i = 0; i < size.options.length; i++) {
        var option = size.options[i];
        var verdict = verdicts[option.value];
        if (verdict && verdict.gated) {
          var message = gateReasonText(verdict, suggestedSize);
          option.disabled = true;
          option.title = message;
          option.setAttribute('aria-label', option.value + ' — not shown in the Fitting Room. ' + message);
        } else {
          option.disabled = false;
          option.removeAttribute('aria-label');
          option.removeAttribute('title');
        }
      }
      // Never leave the try-on control resting on a gated size.
      var current = verdicts[size.value];
      if (current && current.gated) {
        if (verdicts[suggestedSize] && !verdicts[suggestedSize].gated) {
          size.value = suggestedSize;
        } else {
          for (var k = 0; k < size.options.length; k++) {
            if (!size.options[k].disabled) { size.value = size.options[k].value; break; }
          }
        }
      }
    }

    function resetPreview() {
      hideFeedback();
      hideAiCaption();
      requestId += 1;
      clearRetryCountdown();
      stopPhases();
      media.hidden = true;
      image.removeAttribute('src');
      image.alt = '';
      button.disabled = false;
      button.dataset.mode = 'generate';
      button.textContent = 'Take a look in the mirror';
      status.textContent = 'Ready to generate a representative preview.';
      delete status.dataset.errorCode;
      status.classList.add('ws-visually-hidden');
    }

    function update() {
      var person = {
        heightIn: parseInt(height.value, 10),
        weightLb: parseInt(weight.value, 10)
      };
      // suggestion() stays INTERNAL: the gate engine needs the best-fit index to
      // window realistic sizes, but no suggestion is ever surfaced to the shopper.
      suggestedSize = suggestion(garment, person.heightIn, person.weightLb);
      applyGate();
      var resting = !sizeTouched
        || !size.value
        || (size.options[size.selectedIndex] && size.options[size.selectedIndex].disabled);
      if (resting) {
        // Neutral resting point, never labeled a suggestion: the middle of the
        // ungated size run. A size the shopper picked themselves is respected.
        var open0 = [];
        for (var s0 = 0; s0 < size.options.length; s0++) {
          if (!size.options[s0].disabled) open0.push(size.options[s0].value);
        }
        if (open0.length) {
          // Start from the size the shopper already picked on the page when it can be shown;
          // otherwise the neutral middle of the ungated run.
          var pdpPick = pdpSelectedContractSize();
          size.value = (pdpPick && open0.indexOf(pdpPick) !== -1) ? pdpPick : open0[Math.floor(open0.length / 2)];
        }
      }
      refreshSizeRead();
      resetPreview();
    }

    function wait(milliseconds) {
      return new Promise(function (resolve) {
        setTimeout(resolve, milliseconds);
      });
    }

    async function resolvePending(payload, id, deadlineAt, signal) {
      var statusFailures = 0;
      var statusUrl = payload.statusUrl;
      while (payload.status === 'generating' && Date.now() < deadlineAt) {
        if (payload.phase === 'queued') {
          status.textContent = 'Your representative preview is queued. We’ll begin building the selected garment on a frame like yours as soon as a renderer is ready.';
        } else if (payload.phase === 'rendering') {
          status.textContent = 'We’re creating the selected garment and checking its construction, size, and fit before showing it.';
        }
        await wait(2000);
        if (id !== requestId) return null;
        var result;
        try {
          result = await requestStatus(statusUrl, signal);
        } catch (error) {
          if (error && error.name === 'AbortError') throw error;
          statusFailures += 1;
          if (statusFailures >= MAX_STATUS_FAILURES) {
            throw requestError({ code: 'preview_status_unavailable', retryable: true }, 'Preview status is unavailable.');
          }
          if (Date.now() < deadlineAt) continue;
          throw requestError({ code: 'preview_status_invalid', retryable: true }, 'Preview status returned an invalid response.');
        }
        if (result.retryAfter > 0) result.payload.retryAfterSeconds = result.retryAfter;
        if (!result.ok) {
          if (retryableStatus(result.status) && Date.now() < deadlineAt) {
            statusFailures += 1;
            if (statusFailures >= MAX_STATUS_FAILURES) {
              throw requestError({ code: 'preview_status_unavailable', retryable: true, retryAfterSeconds: result.retryAfter }, 'Preview status is unavailable.');
            }
            continue;
          }
          throw requestError(result.payload, 'Preview status is unavailable.');
        }
        statusFailures = 0;
        payload = result.payload;
      }
      if (payload.status === 'generating') {
        throw requestError({ code: 'preview_timeout', retryable: true }, 'Preview generation is taking longer than expected.');
      }
      if (payload.status === 'failed') {
        throw requestError(payload, 'Preview generation failed.');
      }
      return payload;
    }

    button.addEventListener('click', async function () {
      if (button.dataset.mode === 'choose-size') {
        size.focus();
        if (typeof size.showPicker === 'function') {
          try { size.showPicker(); } catch (error) {}
        }
        return;
      }

      var id = ++requestId;
      clearRetryCountdown();
      var retryDelay = 0;
      var selectedProfile = profile();
      var requestDeadlineMs = northstarMode ? 8 * 60 * 1000 : REQUEST_DEADLINE_MS;
      var deadlineAt = Date.now() + requestDeadlineMs;
      var controller = typeof AbortController === 'function' ? new AbortController() : null;
      var abortTimer = controller ? setTimeout(function () { controller.abort(); }, requestDeadlineMs) : null;
      button.disabled = true;
      startPhases(id, selectedProfile.selectedSize);

      try {
        var response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(selectedProfile),
          cache: 'no-store',
          signal: controller ? controller.signal : undefined
        });
        var responseType = (response.headers.get('content-type') || '');
        if (responseType.indexOf('image/') === 0) {
          // Photo-apply responses return the render inline.
          var photoBlob = await response.blob();
          if (id !== requestId) return;
          image.src = URL.createObjectURL(photoBlob);
          media.hidden = false;
        showAiCaption(selectedProfile);
          track('ws_fitting_room_render', { view: (viewSelect && viewSelect.value) || 'front', size: size.value });
          showFeedback(null);
          status.textContent = 'Here is your AI-generated preview.';
          delete status.dataset.errorCode;
          status.dataset.state = 'gated';
          status.classList.remove('ws-visually-hidden');
          button.dataset.mode = 'choose-size';
          button.textContent = 'Want to try another size?';
          return;
        }
        var payload;
        try {
          payload = addRetryHint(await responsePayload(response), response);
        } catch (error) {
          throw requestError({ code: 'preview_response_invalid', retryable: true }, 'Preview generation returned an invalid response.');
        }
        if (id !== requestId) return;
        if (response.status === 202) {
          payload = await resolvePending(payload, id, deadlineAt, controller ? controller.signal : undefined);
          if (!payload) return;
        } else if (!response.ok) {
          throw requestError(payload, 'Preview generation is unavailable.');
        }
        if (payload.gated === true) {
          // This size isn't a realistic fit on this frame; the Fitting Room
          // intentionally does not render it. Show the honest message as an
          // informational state — not an error, no retry countdown.
          media.hidden = true;
          retryDelay = 0;
          delete status.dataset.errorCode;
          status.textContent = payload.message || "That size isn't a realistic fit on this frame, so we don't render it.";
          track('ws_fitting_room_gated', { size: size.value });
          status.dataset.state = 'gated';
          status.classList.remove('ws-visually-hidden');
          button.dataset.mode = 'choose-size';
          button.textContent = 'Try another size';
          return;
        }
        if (payload.status === 'failed') {
          throw requestError(payload, 'Preview generation failed.');
        }
        if (!payload.fit || !payload.imageUrl) {
          throw requestError({ code: 'preview_incomplete', retryable: true }, 'Preview generation returned incomplete data.');
        }

        var rendered = payload.fit.selected || payload.fit.best;
        size.value = rendered.size;
        refreshSizeRead();
        image.src = payload.imageUrl;
        media.hidden = false;
        showAiCaption(selectedProfile, rendered.size);
        track('ws_fitting_room_render', { view: (viewSelect && viewSelect.value) || 'front', size: size.value });
        showFeedback(payload.key || null);
        status.textContent = 'Your representative preview is ready for size ' + rendered.size + '.';
        status.classList.add('ws-visually-hidden');
        button.dataset.mode = 'choose-size';
        button.textContent = 'Want to try another size?';
      } catch (error) {
        if (id === requestId) {
          if (error && error.name === 'AbortError') {
            error = requestError({ code: 'preview_timeout', retryable: true }, 'Preview generation is taking longer than expected.');
          }
          status.dataset.errorCode = error && (error.code || error.name) ? (error.code || error.name) : 'unknown_error';
          retryDelay = error && error.retryable ? (error.retryAfterSeconds || 15) : 0;
          status.textContent = friendlyFailure(error);
          track('ws_fitting_room_error', { code: (error && error.code) || 'unknown' });
          status.dataset.state = 'error';
          status.classList.remove('ws-visually-hidden');
          button.dataset.mode = 'generate';
          button.textContent = 'Try again';
        }
      } finally {
        if (abortTimer) clearTimeout(abortTimer);
        if (id === requestId) {
          var showError = !status.classList.contains('ws-visually-hidden') && status.textContent;
          stopPhases();
          if (showError && status.dataset.state !== 'gated') status.dataset.state = 'error';
          if (retryDelay > 0) startRetryCountdown(id, retryDelay);
          else button.disabled = false;
        }
      }
    });

    open.addEventListener('click', show);
    close.addEventListener('click', hide);
    overlay.addEventListener('cancel', function (event) {
      event.preventDefault();
      hide();
    });
    overlay.addEventListener('click', function (event) {
      if (event.target === overlay) hide();
    });
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && overlay.classList.contains('is-open')) hide();
    });
    // Restore a remembered frame (this device only) before the first update().
    (function () {
      var f = loadFrame();
      if (!f) return;
      function setIf(sel, val) { if (sel && val != null && val !== '' && [].some.call(sel.options, function (o) { return o.value === String(val); })) sel.value = String(val); }
      setIf(height, f.heightIn); setIf(weight, f.weightLb);
      // Representation choices are never restored, and any left by an earlier version are purged
      // from this device rather than left sitting there.
      if (f.gender || f.genderSelf || f.ethnicity || f.ethnicitySelf) {
        saveFrame({ heightIn: f.heightIn, weightLb: f.weightLb, savedAt: f.savedAt || new Date().toISOString() });
      }
      if (forgetBtn) forgetBtn.hidden = false;
    })();
    // Follow the purchase selector: a size picked on the page moves the Fitting Room's resting
    // size until the shopper touches the Fitting Room's own size control.
    document.addEventListener('change', function (event) {
      var t = event.target;
      if (!t || !t.classList || !t.classList.contains('ws-buybox__radio')) return;
      if (String(t.dataset.wsOptionIndex) !== String(pdpSizeOptionIndex)) return;
      if (!sizeTouched) update();
    });
    height.addEventListener('change', update);
    weight.addEventListener('change', update);
    size.addEventListener('change', function () {
      sizeTouched = true;
      refreshSizeRead();
      resetPreview();
    });
    update();
  }

  function boot() {
    document.querySelectorAll('[data-ws-fresh-fit]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();

